import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { donationOptions } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Heart, Shield, Award, CheckCircle, X, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

// Donor testimonials data
const donorTestimonials = [
  {
    id: 1,
    text: "As siblings, we've always believed in giving back to the world in ways that truly matter – and helping voiceless animals is something that connects deeply with both of us. We came across this incredible shelter doing selfless work for injured, abandoned, and abused animals, and it immediately struck a chord. Watching their stories of rescue and recovery, we knew this was a cause worth supporting. Every little contribution we make feels like a step towards a kinder world.",
    name: "Priyansha"
  },
  {
    id: 2,
    text: "I had the opportunity to visit the shelter, and I must say – no video or photo could have prepared me for the powerful experience it was. I saw dogs who had been through unimaginable pain now resting peacefully, wagging their tails at the team who saved them. The shelter isn't just a rescue center – it's a home full of hope, healing, and second chances. The team works round the clock, often in difficult conditions, yet their compassion never fades. It made me realize that our support isn't just appreciated – it's essential.",
    name: "Divyansh"
  },
  {
    id: 3,
    text: "Compassion has no boundaries, and this shelter proves that every single day. What touched me most was not just the rescue stories, but the quiet dignity with which each life is treated. Whether it's a critically injured dog or a paralyzed puppy, they're cared for with the same love and patience. I often find myself looking forward to their updates — they don't just show recovery, they show resilience. Supporting this shelter has given me more than satisfaction — it's given me hope. Hope that kindness still exists in this world, and that we can all be part of it in our own way.",
    name: "Harroop Kaur"
  },
  {
    id: 4,
    text: "There's something truly special about this shelter — a sense of purpose that goes beyond rescue. What drew me in was their genuine concern for even the smallest, often overlooked animals. Be it their feeding drives, the sterilization efforts, or simply placing water bowls during heatwaves — they care. Deeply. And that sincerity comes through in everything they do. Donating felt like the natural thing to do, but staying connected with their work has become something I cherish. It's not just about helping animals — it's about being part of a movement that chooses compassion every single day.",
    name: "Priyanka Shekhawat"
  },
  {
    id: 5,
    text: "What I admire most about this shelter is how deeply they care — not just for injured animals, but for every living being struggling to survive. I still remember the water bowl campaign they shared on Instagram — placing clean water bowls across public spaces for stray animals and birds during the scorching summer. It moved me. Such a simple act, yet it spoke volumes about their commitment to kindness. Their updates are more than stories — they're daily reminders of empathy in action. Supporting them makes me feel like I'm part of something meaningful, even from afar. Every post gives hope, and every campaign inspires action.",
    name: "Shalini"
  },
  {
    id: 6,
    text: "There's something truly powerful about the way this shelter tells its stories. Each update, each rescued life shared on Instagram, reminds me of the impact compassion can make. I've seen animals go from pain to peace, from abandonment to care — and it's deeply moving. What stands out is the honesty and heart behind every post. It's not just about rescue; it's about rebuilding lives with dignity. Supporting this shelter has been one of the most meaningful choices I've made, and it continues to remind me that kindness, when consistent, can truly transform the world — one animal at a time.",
    name: "Bhupender Singh"
  },
  {
    id: 7,
    text: "Visiting the Animal Care Trust shelter was a deeply moving experience. Seeing the dedication of the team, the cleanliness of the shelter, and the love given to each injured and abandoned animal truly touched my heart. I could see the difference every donation makes — from life-saving surgeries to warm meals for the rescues. I'm proud to support such a compassionate and impactful organization. Thank you for letting me be a small part of this incredible journey!",
    name: "Payal Chopra"
  },
  {
    id: 8,
    text: "We are truly grateful to the Animal Care Team for their incredible support in vaccinating and deworming all our dogs. Your dedication, compassion, and professionalism made the entire process smooth and stress-free for both the animals and for us. Teams like yours bring real change and hope for the voiceless. Thank you for your tireless efforts and for always standing with our dogs. Recently, one of our community dogs was badly injured with severe bite wounds. After reaching out to many NGOs and shelters without much response, I decided to seek help from the Animal Care Team. To my surprise, they responded within half an hour, carefully consulted about the case, and immediately guided me with a simple and effective process to help the dog. I will always remain deeply thankful to the Animal Care Team for their unwavering support.",
    name: "Samiksha Dixit"
  },
  {
    id: 9,
    text: "It gives me immense satisfaction to support Animal Care Trust. The team's relentless effort in rescuing and healing injured animals shows true compassion in action. Knowing that my contribution helps save lives and gives these voiceless beings a second chance fills me with gratitude.",
    name: "Suraj Cankapalli"
  },
  {
    id: 10,
    text: "Being associated with Animal Care Trust has been an inspiring journey. Every animal that is rescued and cared for reflects the love and dedication of the team. I am proud to contribute towards their mission and truly believe that together we can create a kinder world for animals.",
    name: "Meenakshi Das"
  },
  {
    id: 11,
    text: "Supporting Animal Care Trust has been one of the most fulfilling choices I've made. Watching how the team turns pain into healing — and despair into second chances — gives me faith in humanity again. It's incredible to know that a small act of giving can create such big changes.",
    name: "Anuj Sharma"
  },
  {
    id: 12,
    text: "I am incredibly honored to support the remarkable work being done by Animal Care. Their unwavering dedication to rescuing, rehabilitating, and providing lifelong care for injured, abandoned, and special-needs animals is truly inspiring. Each time I visit or hear updates from the shelter, I am reminded of the profound impact they have on countless lives—not just the animals, but the communities they serve as well. Animal Care's work extends beyond just treatment and rehabilitation. Their daily feeding programs, life-saving medical interventions, and commitment to long-term care reflect their deep compassion and professionalism. Knowing that my donations contribute to food, medical care, shelter maintenance, and staff support fills me with immense pride. I wholeheartedly encourage others to support Animal Care's mission. Their work is not just about saving animals—it's about restoring hope, dignity, and life to those who need it most. It is a privilege to be a part of such an extraordinary journey.",
    name: "Reema Chawla"
  },
  {
    id: 13,
    text: "Supporting Animal Care Shelter has been one of the most fulfilling decisions I've made. The team's dedication and compassion are truly inspiring—they don't just rescue animals, they give them a second chance at life. I've followed stories like Sunahari's, who survived a brutal attack and fully recovered under their care, and Bhuri, who lost a leg in an accident but found safety and love at the shelter. Knowing my contributions help provide food, medical care, and shelter for these innocent souls gives me immense satisfaction. Every animal here has a story of resilience, and Animal Care Shelter's efforts remind me that kindness can truly change lives. I'm proud to be part of this mission and encourage others to support their amazing work. Together, we can make a world of difference for these voiceless beings.",
    name: "Sujata Mohapatra"
  }
];

const Donate = () => {
  const [searchParams] = useSearchParams();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  // Razorpay payment page URLs
  const RAZORPAY_MONTHLY_URL = 'https://pages.razorpay.com/pl_Pj0yvKpaF4Y1uE/view';
  const RAZORPAY_ONETIME_URL = 'https://rzp.io/rzp/LiJlRniI';

  // Track referral source
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      sessionStorage.setItem('donation_referral', ref);
    }
  }, [searchParams]);

  // Check for payment success from URL params (Razorpay redirect)
  useEffect(() => {
    const paymentId = searchParams.get('razorpay_payment_id');
    if (paymentId) {
      setPaymentSuccess(true);
      setPaymentDetails({ paymentId });
    }
  }, [searchParams]);

  // Auto-play testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % donorTestimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Testimonial navigation
  const nextTestimonial = () => {
    setTestimonialIndex((prev) => (prev + 1) % donorTestimonials.length);
  };

  const prevTestimonial = () => {
    setTestimonialIndex((prev) => (prev - 1 + donorTestimonials.length) % donorTestimonials.length);
  };

  const goToTestimonial = (index) => {
    setTestimonialIndex(index);
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Payment Success Modal */}
      {paymentSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full text-center shadow-2xl relative animate-bounce-in">
            <button
              onClick={() => setPaymentSuccess(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="w-16 h-16 md:w-20 md:h-20 bg-bright-mint rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
              <CheckCircle className="w-10 h-10 md:w-12 md:h-12 text-forest-green" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-forest-green mb-4">
              Thank You! 🎉
            </h2>
            <p className="text-lg md:text-xl text-gray-700 mb-4">
              Your donation was successful!
            </p>
            <p className="text-sm md:text-base text-gray-600 mb-6">
              You are now helping save animal lives. We'll send you updates on how your contribution makes a difference.
            </p>
            {paymentDetails?.paymentId && (
              <p className="text-xs md:text-sm text-gray-500 mb-4">
                Payment ID: {paymentDetails.paymentId}
              </p>
            )}
            <Button
              onClick={() => setPaymentSuccess(false)}
              className="bg-forest-green hover:bg-green-700 text-white px-6 md:px-8 py-2 md:py-3"
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Animated Animals Background */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute bottom-10 animate-run-right">
          <span className="text-4xl md:text-6xl opacity-20">🐕</span>
        </div>
        <div className="absolute bottom-20 animate-run-left" style={{ animationDelay: '3s' }}>
          <span className="text-3xl md:text-5xl opacity-20">🐈</span>
        </div>
        <div className="absolute bottom-32 animate-run-right" style={{ animationDelay: '5s' }}>
          <span className="text-2xl md:text-4xl opacity-15">🐕‍🦺</span>
        </div>
        <div className="absolute bottom-5 left-0 right-0 flex justify-around opacity-10">
          {[...Array(10)].map((_, i) => (
            <span key={i} className="text-xl md:text-2xl" style={{ animationDelay: `${i * 0.5}s` }}>🐾</span>
          ))}
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-forest-green to-green-600 text-white relative z-10">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-heading mb-4 md:mb-6">
            Every Donation Saves Lives
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto mb-6 md:mb-8 px-2">
            Your donation provides emergency care, surgeries, food, and shelter to animals in desperate need.
          </p>
          <div className="flex items-center justify-center space-x-2 text-sm md:text-lg">
            <Shield className="w-5 h-5 md:w-6 md:h-6" />
            <span>Every donation goes directly to animal welfare</span>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 md:py-20 bg-white relative z-10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-heading text-forest-green mb-2 md:mb-4">
              Why Donate to Us?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-warm-amber rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Shield className="w-8 h-8 md:w-10 md:h-10 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-forest-green mb-2">100% Transparency</h3>
              <p className="text-sm md:text-base text-gray-700">Regular updates through our newsletter on website</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-forest-green rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Award className="w-8 h-8 md:w-10 md:h-10 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-forest-green mb-2">80G Tax Benefits</h3>
              <p className="text-sm md:text-base text-gray-700">Get tax exemption certificates for your donations</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-bright-mint rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <Heart className="w-8 h-8 md:w-10 md:h-10 text-forest-green" fill="currentColor" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-forest-green mb-2">Direct Impact</h3>
              <p className="text-sm md:text-base text-gray-700">Every donation goes directly to animal welfare</p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Breakdown */}
      {/* <section className="py-12 md:py-20 bg-white relative z-10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-heading text-forest-green mb-2 md:mb-4">
              Your Impact
            </h2>
            <p className="text-base md:text-xl text-gray-700">See how your donation makes a difference</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 max-w-6xl mx-auto">
            {donationOptions.map((option) => (
              <Card key={option.id} className="border-2 border-transparent hover:border-warm-amber transition-all duration-300 shadow-lg">
                <CardContent className="p-4 md:p-6 text-center">
                  <div className="text-2xl md:text-4xl font-bold text-warm-amber font-heading mb-2 md:mb-3">
                    ₹{option.amount}
                  </div>
                  <p className="text-gray-700 text-sm md:text-base leading-relaxed">{option.impact}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* Razorpay Donation Section */}
      <section className="py-12 md:py-20 bg-sand-beige relative z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-stretch">
            <Card className="border-none shadow-2xl">
              <CardContent className="p-6 md:p-10">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-forest-green mb-6 md:mb-8 text-center">
                  Make Your Donation
                </h2>

                <p className="text-center text-gray-600 mb-6">
                  Choose how you'd like to support our mission
                </p>

                {/* Donation Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <a
                    href={RAZORPAY_ONETIME_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-6 px-6 rounded-xl font-bold text-lg bg-warm-amber hover:bg-amber-600 text-white shadow-lg transition-all duration-200 hover:scale-105 flex flex-col items-center text-center no-underline"
                  >
                    <Heart className="w-8 h-8 mb-2" fill="white" />
                    <span>One-Time Donation</span>
                    <span className="text-sm font-normal mt-1 opacity-90">Give once, make an impact</span>
                    <ExternalLink className="w-4 h-4 mt-2 opacity-70" />
                  </a>
                  <a
                    href={RAZORPAY_MONTHLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-6 px-6 rounded-xl font-bold text-lg bg-forest-green hover:bg-green-700 text-white shadow-lg transition-all duration-200 hover:scale-105 flex flex-col items-center text-center no-underline"
                  >
                    <Heart className="w-8 h-8 mb-2" fill="white" />
                    <span>Monthly Donation</span>
                    <span className="text-sm font-normal mt-1 opacity-90">Recurring support</span>
                    <ExternalLink className="w-4 h-4 mt-2 opacity-70" />
                  </a>
                </div>

                <div className="text-center text-gray-600 mt-6">
                  <p className="text-xs md:text-sm">Secure payment powered by Razorpay</p>
                  <p className="text-xs text-gray-400 mt-2">80G Tax Benefits Available</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-2xl">
              <CardContent className="p-6 md:p-10 flex flex-col items-center justify-center h-full">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-forest-green mb-4 md:mb-6 text-center">
                  Scan to Donate
                </h2>
                <div className="bg-white p-0.5 rounded-xl shadow-md border border-gray-100">
                  <img
                    src="/qr.png"
                    alt="QR code to donate"
                    className="w-48 h-48 md:w-56 md:h-56 object-contain"
                  />
                </div>
                <p className="text-center text-gray-600 mt-4 md:mt-6 text-sm md:text-base">
                  Scan the QR code with any UPI app to donate instantly
                </p>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Supports Google Pay, PhonePe, Paytm & more
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Donor Testimonials Slider */}
      <section className="py-12 md:py-20 bg-white relative z-10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-heading text-forest-green mb-2 md:mb-4">
              What Our Donors Say
            </h2>
          </div>

          <div className="max-w-4xl mx-auto relative">
            {/* Navigation Arrows */}
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-12 z-10 w-10 h-10 md:w-12 md:h-12 bg-forest-green hover:bg-green-700 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-12 z-10 w-10 h-10 md:w-12 md:h-12 bg-forest-green hover:bg-green-700 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            {/* Testimonial Card */}
            <div className="overflow-hidden px-8 md:px-0">
              <Card className="border-none shadow-lg bg-sand-beige min-h-[280px] md:min-h-[240px]">
                <CardContent className="p-6 md:p-8">
                  <div className="text-4xl md:text-5xl text-warm-amber mb-4">"</div>
                  <p className="text-gray-700 italic mb-4 md:mb-6 text-sm md:text-base leading-relaxed transition-opacity duration-500">
                    {donorTestimonials[testimonialIndex].text}
                  </p>
                  <p className="font-bold text-forest-green text-base md:text-lg">
                    — {donorTestimonials[testimonialIndex].name}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Dot Indicators */}
            <div className="flex justify-center mt-6 gap-2 flex-wrap">
              {donorTestimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-300 ${index === testimonialIndex
                    ? 'bg-warm-amber w-6 md:w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes runRight {
          0% { transform: translateX(-100px) scaleX(1); }
          100% { transform: translateX(calc(100vw + 100px)) scaleX(1); }
        }
        @keyframes runLeft {
          0% { transform: translateX(calc(100vw + 100px)) scaleX(-1); }
          100% { transform: translateX(-100px) scaleX(-1); }
        }
        @keyframes bounceIn {
          0% { transform: scale(0.5); opacity: 0; }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-run-right {
          animation: runRight 15s linear infinite;
        }
        .animate-run-left {
          animation: runLeft 18s linear infinite;
        }
        .animate-bounce-in {
          animation: bounceIn 0.5s ease-out;
        }
        .razorpay-button-wrapper {
          min-height: 50px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .razorpay-button-wrapper form {
          display: flex;
          justify-content: center;
        }
      `}</style>
    </div>
  );
};

export default Donate;